import AudienceQrCode from "./audienceQrCode";
import CopyAudienceLink from "./copyAudienceLink";
import useAuth from "hooks/useAuth";
import useEstatesAndMaps from "hooks/useEstatesAndMaps";
import { getAudienceBaseUrl, getAudienceUrl } from "utils/audienceBranding";
import { getInternalAppType } from "utils/brandingUtils";
import { useState, SyntheticEvent, useEffect } from "react"

// material ui
import {
   Divider, Box, Grid, Typography, TextField, createFilterOptions, Switch, Stack,
} from '@mui/material';
import { SelectChangeEvent } from "@mui/material/Select";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// project import
import config from "config/config";
import { DEFAULT_CATEGORIES, DISABLE_AUDIENCE } from "config/config-general";
import FormDialog from 'components/dialogs/FormDialog';
import Selector from 'components/select/selector';
import { useTranslation } from 'utils/locales/utilityFunctions';
import { validateEmail } from "utils/textModification";
import AutoCompleteCreatable from "components/input/autoCompleteCreatable";
import AutoCompleteTags from 'components/input/autoCompleteTags';

import { EstateProps, MapProps, ProjectProps } from "types/estatesAndMap";
import { RoleTypes, UserProfile } from "types/auth";
import AutoCompleteGrouped from "components/input/autoCompleteGrouped";
import dayjs from "dayjs";
import 'dayjs/locale/da';
import { getShortestNestedArrayObject } from "utils/dataManipulation";
import { ChipData } from "types/list";

export type InfoElementProps = {
   id: string,
   label: string,
}

export interface ContactType {
   personType?: "team" | "contact" | "audience",
   name?: string,
   nameAndOrgName?: string,
   email?: string,
   id?: string,
   role?: RoleTypes,
   label: string,
   inputValue?: string,
   status?: string,
   date?: string | null,
   categories?: string[] | null,
   inviterId?: string,
   ignoreMaps?: ChipData[],
   ignoreProjects?: ChipData[],
   orgId?: string,
   orgName?: string,
}

interface CategoriesType {
   id: string,
   label: string,
}

type ShareDialogProps = {
   open: boolean,
   user: UserProfile,
   withDataSelector: boolean,
   withPersonSelector: boolean,
   withAudience: boolean,
   loading?: boolean,
   dataType: null | "map" | "estate" | "project",
   allRoleOptions: { id: RoleTypes, status: string, label: string }[],
   dataOptions: { list: any[], uniqueEstates: string[], estateIdsAndNames?: { [key: string]: string } },
   selectedPerson?: null | ContactType,
   teamList?: null | ContactType[],
   contactList?: null | ContactType[],
   selectedData?: null | EstateProps | MapProps | ProjectProps,
   estateMaps?: null | MapProps[],
   estateProjects?: null | ProjectProps[],
   categoriesFromData?: null | string[],
   handleChangeSelectedData?: (data: any | null) => void,
   handleClose: () => void,
   handleSave: (persons: ContactType[], data: EstateProps | MapProps | ProjectProps) => void,
}

const filterContacts = createFilterOptions<ContactType>();

const ShareDialog = ({ open, user, withDataSelector, withPersonSelector, withAudience, loading, dataType, allRoleOptions, dataOptions, selectedPerson, teamList, contactList, selectedData, estateMaps, categoriesFromData, handleClose, handleSave, handleChangeSelectedData }: ShareDialogProps) => {
   // --- Hooks --- //
   const { translate } = useTranslation();
   const { userOrg } = useAuth();
   const { estates, orgEstates } = useEstatesAndMaps();
   const audienceBaseUrl = getAudienceBaseUrl(config.ENVIRONMENT, getInternalAppType({ pathOrHost: window.location.hostname }));
   const audienceBrandingPackageId = userOrg?.subscription?.brandingPackageId || userOrg?.defaultBrandingPackageId || undefined;
   const mapBrandingPackageId = (estateId?: string) => {
      const estate = estateId ? estates[estateId] || orgEstates[estateId] : undefined;
      return estate?.orgId === userOrg?.id ? audienceBrandingPackageId : undefined;
   };
   const isAudienceContact = withAudience && selectedPerson?.personType === "audience";
   const isNewAudienceSharing = isAudienceContact && withDataSelector;
   const isEditingAudienceSharing = isAudienceContact && !withDataSelector;
   const sendInviteText = selectedPerson ? translate("share") : translate("send-invite");

   // --- Data --- //
   const TEAM_DATA: ContactType[] = teamList ? teamList as ContactType[] : [];
   const CONTACTS_DATA: ContactType[] = contactList ? contactList as ContactType[] : [];
   const DATA = [...TEAM_DATA, ...CONTACTS_DATA];
   const CATEGORIES_DATA: CategoriesType[] = categoriesFromData ? categoriesFromData.map(el => {
      let label = el;
      if ((DEFAULT_CATEGORIES as unknown as string[]).includes(el)) {
         label = translate("tag-" + el);
      }
      return { id: el, label: label };
   }) : [];

   let title = translate("share");
   if (!withDataSelector && selectedData && selectedData.name) {
      title = selectedData.name;
   }

   // --- State --- //
   const [roleOptions, setRoleOptions] = useState<{ id: RoleTypes, label: string }[]>([]);
   const [role, setRole] = useState<RoleTypes>(selectedPerson && selectedPerson.role ? selectedPerson.role : allRoleOptions[0].id);
   const [date, setDate] = useState<Date | null>(null);
   const [audienceToggle, setAudienceToggle] = useState(selectedData ? selectedData.audienceActive : false);
   const [disableSendButton, setDisableSendButton] = useState(true);
   const [categoriesActive, setCategoriesActive] = useState(false);
   const [dateActive, setDateActive] = useState(false);
   const [chosenContacts, setChosenContacts] = useState<ContactType[]>(selectedPerson ? [selectedPerson] : [])
   const [chosenData, setChosenData] = useState<any[]>([])
   const [categories, setCategories] = useState<CategoriesType[]>([]);
   const [publicUrls, setPublicUrls] = useState<{ title: string, url: string }[]>([]);
   const [inviterId, setInviterId] = useState<string | undefined>(undefined);

   // --- Effects --- //
   useEffect(() => {
      if (!open) {
         resetState();
      }
   }, [open])
   // Set public urls based on type
   useEffect(() => {
      if (dataType === "estate" && estateMaps) {
         setPublicUrls(estateMaps.map(map => {
            const url = getAudienceUrl(audienceBaseUrl, map.id, mapBrandingPackageId(map.estateId));
            return {
               title: map.name,
               url: url.toString(),
            }
         }));
      } else if (dataType === "map" && selectedData) {
         const url = getAudienceUrl(audienceBaseUrl, selectedData.id, mapBrandingPackageId((selectedData as MapProps).estateId));
         setPublicUrls([{
            title: selectedData.name,
            url: url.toString(),
         }])
      } else setPublicUrls([]);

   }, [dataType, estateMaps, selectedData, estates, orgEstates, userOrg?.id, audienceBrandingPackageId, audienceBaseUrl])

   useEffect(() => {
      if (!open) {
         if (chosenData.length > 0) setChosenData([]);
         if (chosenContacts.length > 0) setChosenContacts([]);
         if (role !== allRoleOptions[0].id) setRole(allRoleOptions[0].id);
      }
   }, [open])

   useEffect(() => {
      if (withDataSelector) {
         if (chosenData.length > 0) {
            // Check if categories are in data
            const catIn: string[] = chosenData[0].categories ? chosenData[0].categories : null;
            if (catIn) {
               const cat: CategoriesType[] = catIn ? catIn.map(el => {
                  let label = el;
                  if ((DEFAULT_CATEGORIES as unknown as string[]).includes(el)) {
                     label = translate("tag-" + el);
                  }
                  return { id: el, label: label }
               }) : [];
               setCategories(cat) 
            }
            // Check for expire date in data
            const dateIn = chosenData[0].expireDate ? new Date(chosenData[0].expireDate) : null;
            if (dateIn) setDate(dateIn);
            // Check for inviter id (set if one us sharing a map formerly shared with a team member)
            if (chosenData[0].inviterId) {
               setInviterId(chosenData[0].inviterId);
            }
            if (disableSendButton) {
               setDisableSendButton(false);
            }
         } else {
            if (!disableSendButton) {
               setDisableSendButton(true);
            }
         }
      } else {
         if (chosenData.length > 0) {
            setChosenData([]);
         }
         if (selectedPerson && selectedData) {
            if (selectedPerson.personType === "contact") {
               // Check if contact is represented in the selected data roles
               if (selectedPerson.status !== "invite-pending" && selectedData.roles && selectedPerson.id && selectedData.roles[selectedPerson.id]) {
                  // Check for categories in data
                  if (selectedData.roles[selectedPerson.id].categories) {
                     const catIn = selectedData.roles[selectedPerson.id].categories ? selectedData.roles[selectedPerson.id].categories : [];
                     const cat: CategoriesType[] = catIn ? catIn.map(el => {
                        let label = el;
                        if ((DEFAULT_CATEGORIES as unknown as string[]).includes(el)) {
                           label = translate("tag-" + el);
                        }
                        return { id: el, label: label }
                     }) : [];
                     setCategories(cat)
                  }
                  // Check for expiredate in data
                  if (selectedData.roles[selectedPerson.id].expireDate) {
                     setDate(selectedData.roles[selectedPerson.id].expireDate ? new Date(selectedData.roles[selectedPerson.id].expireDate as string) : null);
                  }
               } else {
                  if (selectedPerson.categories) {
                     const cat: CategoriesType[] = selectedPerson.categories.map(el => {
                        let label = el;
                        if ((DEFAULT_CATEGORIES as unknown as string[]).includes(el)) {
                           label = translate("tag-" + el);
                        }
                        return { id: el, label: label }
                     });
                     setCategories(cat);
                  }
                  if (selectedPerson.date) {
                     setDate(new Date(selectedPerson.date));
                  }
                  if (selectedPerson.role) {
                     setRole(selectedPerson.role);
                  }
               }
               if (!categoriesActive) setCategoriesActive(true);
               if (!dateActive) setDateActive(true);
            }
            if (disableSendButton) {
               setDisableSendButton(false);
            }
         }
      }
      if (selectedData) {
         // Check for selected data type (estate, map, project)
         let roleOptions = [allRoleOptions[0]];
         let role = allRoleOptions[0].id;
         // Get selected data role according to selected person
         if (dataType === "estate") {
            const mapAndProjRoles = dataOptions.list.filter(el => el.estateId === selectedData.id);
            const selDataOpt = getShortestNestedArrayObject(mapAndProjRoles, "roleOptions");
            if (selDataOpt) {
               roleOptions = selDataOpt.roleOptions;
               role = selDataOpt.role ? selDataOpt.role : role;
            }
         } else if (dataType === "map") {
            // Set role options according to current user role
            const selDataOpt = dataOptions.list.filter(el => el.mapId === selectedData.id);
            if (selDataOpt.length > 0)  {
               roleOptions = selDataOpt[0].roleOptions;
               role = selDataOpt[0].role ? selDataOpt[0].role : role;
            }
         } else if (dataType === "project" ) {
            const selDataOpt = dataOptions.list.filter(el => el.projectId === selectedData.id);
            if (selDataOpt.length > 0) {
               roleOptions = selDataOpt[0].roleOptions;
               role = selDataOpt[0].role ? selDataOpt[0].role : role;
            } 
         }
         // Check for selected person and pserson type
         if (selectedPerson && selectedPerson.personType === "contact") {
            if (!categoriesActive) setCategoriesActive(true);
            if (!dateActive) setDateActive(true);
         } else {
            if (categoriesActive) setCategoriesActive(false);
            if (dateActive) setDateActive(false);
         }
         setRoleOptions(roleOptions);
         setRole(role);
         if (selectedData.audienceActive !== audienceToggle) {
            setAudienceToggle(selectedData.audienceActive);
         }
      } else {
         setRoleOptions([allRoleOptions[0]]);
      }
   }, [withDataSelector, selectedPerson, selectedData, chosenData, open])

   // --- Functions --- //
   const resetState = () => {
      setRole(allRoleOptions[0].id);
      setDate(null);
      setAudienceToggle(false);
      setDisableSendButton(true);
      setCategoriesActive(false);
      setDateActive(false);
      setChosenContacts([]);
      setChosenData([]);
      setCategories([]);
   }

   const closeHandler = () => {
      setDisableSendButton(true);
      handleClose();
   }

   const shareAction = async () => {
      // Check if person sharing data has categories and expire date
      // Set same data to the people he is sharing with
      let catIn = categories ? categories.map(el => el.id) : null;
      let dateIn = date ? dayjs(date).format() : null;
      
      if (selectedPerson && user && selectedPerson.id !== user.id) {
         if (selectedData?.roles && selectedData.roles[user.id!]) {
            catIn = (selectedData.roles[user.id!].categories && selectedData.roles[user.id!].categories!.length > 0) ? selectedData.roles[user.id!].categories as string[] : catIn;
            dateIn = selectedData.roles[user.id!].expireDate ? selectedData.roles[user.id!].expireDate as string : dateIn;
         }
      }
      if (selectedPerson && selectedData) {
         const persons: ContactType[] = [
            {
               ...selectedPerson,
               inviterId: inviterId,
               inputValue: isNewAudienceSharing || audienceToggle ? "true" : "false",
               role: role,
               date: dateIn ? dayjs(dateIn).format() : null,
               categories: catIn,
            }
         ];
         handleSave(persons, { ...selectedData });
      } else if (selectedData) {
         const persons: ContactType[] = [
            ...chosenContacts.map(el => {
               return {
                  ...el,
                  role: role,
                  date: dateIn ? dayjs(dateIn).format() : null,
                  categories: catIn
               }
            }),
            { personType: "audience", label: "audience", inputValue: audienceToggle ? "true" : "false", role: role }
         ]
         handleSave(persons, { ...selectedData });
      }
   }

   const handleChangeShareData = (e: any, v: any) => {
      // Check for values i the select array
      if (handleChangeSelectedData) {
         if (v.length === 0) {
            handleChangeSelectedData(null);
            setChosenData([]);
            return;
         }
         const lastItem = v.slice(-1)[0];
         handleChangeSelectedData(lastItem);
         setChosenData([lastItem]);
      }
   }

   const handleChangeRole = (e: SelectChangeEvent<string>) => {
      setRole(e.target.value as RoleTypes);
   }

   const handleChangeContactPerson = (e: SyntheticEvent<Element, Event>, v: ContactType[]) => {
      // Check incomming value
      const inArray = [...v];
      const lastEntry = inArray.pop();
      if (v.length === 0) {
         setDisableSendButton(true);
      }
      if (lastEntry && lastEntry.inputValue === user.email) {
         setChosenContacts(inArray);
      } else if (lastEntry && !lastEntry.label) {
         if (inArray.length > 0) {
            setDisableSendButton(false);
         }
         setChosenContacts(inArray);
      } else {
         if (v.length > 0) {
            setDisableSendButton(false);
         }
         setChosenContacts(v);
      }
      // Check for contacts in value array
      setCategoriesActive(false);
      setDateActive(false);
      for (const person of v) {
         if (person.personType === "contact") {
            setCategoriesActive(true);
            setDateActive(true);
            break;
         }
      }
   }

   const handleContactFilter = (options: any, params: any) => {
      const filtered = filterContacts(options, params);
      const { inputValue } = params;
      // Check for existing option
      const isExisting = options.some((option: ContactType) => inputValue === option.email);
      // Check input to be email address
      const validEmail = validateEmail(inputValue);
      if (validEmail && !isExisting) {
         filtered.push({
            inputValue,
            email: inputValue,
            label: inputValue,
            name: inputValue,
            role: role,
            personType: "contact",
         })
      }
      return filtered;
   }

   const handleChangeCategories = (e: SyntheticEvent<Element, Event>, v: any) => {
      setCategories(v)
   }


   const handleChangeAudienceToggle = (e: SyntheticEvent<Element, Event>, v: any) => {
      setDisableSendButton(false);
      setAudienceToggle(v);
   }

   // --- Return JSX --- //
   return (
      <FormDialog
         open={open}
         title={title}
         onClickText={isEditingAudienceSharing ? translate("save") : sendInviteText}
         onCancelText={translate("cancel")}
         onClick={shareAction}
         onCancel={closeHandler}
         saveDisabled={disableSendButton || (isAudienceContact && DISABLE_AUDIENCE)
            || (isEditingAudienceSharing && Boolean(audienceToggle) === Boolean(selectedData?.audienceActive))}
         loading={loading}
         maxWidth='sm'
      >
         <>
            {/* Data selector */}
            {withDataSelector && <Box sx={{ pb: !selectedData && !withPersonSelector ? 2 : 0 }}>
               <Typography variant="h5" sx={{ mb: 2 }}>
                  {translate("share-map")}
               </Typography>

               <Grid container spacing={2}>
                  <Grid item xs={12}>
                     <AutoCompleteGrouped
                        title="title"
                        label={translate("choose-estate-map")}
                        showTitle={false}
                        showLabel={false}
                        data={dataOptions.list}
                        groupByField={"estateName"}
                        // groupByFieldToLabel={dataOptions.estateIdsAndNames}
                        renderOptionField={"mapName"}
                        renderTagField={"tagField"}
                        noOptionsText={translate("no-results")}
                        changeHandler={handleChangeShareData}
                        value={chosenData}
                        condensed={false}
                        multiple={true}
                     />
                  </Grid>
               </Grid>
            </Box>}

            {/* Person and role selector */}
            {withPersonSelector && <Box sx={{ flexGrow: 1, mt: withDataSelector ? 2 : 0, mb: 2 }}>
               <Typography variant="h5" sx={{ mb: 2 }}>
                  {translate("share-with-team-contacts")}
               </Typography>

               <Grid container spacing={2}>
                  <Grid item sm={8} xs={12}>
                     <AutoCompleteCreatable
                        title={translate("input-email-or-name")}
                        label={translate("input-email-or-name")}
                        showLabel={true}
                        showTitle={false}
                        groupByField="personTypeLabelText"
                        // groupByField="personTypeLabel"
                        renderOptionField="name"
                        renderTagField="name"
                        data={DATA}
                        value={selectedPerson ? [selectedPerson] : chosenContacts}
                        changeHandler={handleChangeContactPerson}
                        filterHandler={handleContactFilter}
                        multiple={true}
                        disabled={selectedPerson ? true : false}
                        filterSelectedOptions={true}
                     />
                  </Grid>

                  <Grid item sm={4} xs={12}>
                     <Selector
                        title={translate("role")}
                        value={role}
                        options={roleOptions}
                        label={true}
                        fullWidth={true}
                        changeHandler={handleChangeRole}
                        required={true}
                        disabled={(selectedPerson ? false : chosenContacts.length === 0) || !selectedData}
                     />
                  </Grid>

                  {categoriesActive && <Grid item sm={8} xs={12}>
                     <AutoCompleteTags
                        title={translate('area-tags')}
                        label={translate('area-tags')}
                        value={categories}
                        data={CATEGORIES_DATA}
                        changeHandler={handleChangeCategories}
                        showLabel={true}
                        noOptionsText={translate('no-more-options-in-list')}
                        multiple={true}
                     />
                  </Grid>}

                  {dateActive && <Grid item sm={4} xs={12}>
                     <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale('da')}>
                        <DatePicker
                           label={translate("expire-date")}
                           value={date}
                           onChange={(newValue) => {
                              setDate(newValue);
                           }}
                           renderInput={(params) => <TextField
                              color="secondary"
                              {...params}
                              fullWidth
                              sx={{
                                 '& .MuiFormLabel-root': { fontSize: 14.5 },
                                 "& label": {
                                    marginTop: date !== null ? "0px" : "-5px",
                                    "&.Mui-focused": {
                                       marginTop: "0px"
                                    },
                                    color: 'secondary.600'
                                 }
                              }}
                           />}
                        />
                     </LocalizationProvider>
                  </Grid>}
               </Grid>
            </Box>}

            {withAudience && (!selectedPerson || selectedPerson.personType === "audience") && (dataType === "map" || dataType === "estate") && <>
               {(withDataSelector || withPersonSelector) && <Divider sx={{ my: 2 }} />}

               <Box sx={{ flexGrow: 1 }}>
                  <Stack direction={"row"} gap={1}>
                     <Typography variant="h5" sx={{ mb: 1 }}>
                        {translate("share-audience-map")}
                     </Typography>

                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                     {translate(isNewAudienceSharing ? "share-audience-contact-guidance" : "share-audience-tag-guidance")}
                  </Typography>

                  <Grid container spacing={2} wrap="nowrap">
                     <Grid item xs sx={{ minWidth: 0 }}>
                        {isAudienceContact && dataType === "estate" && publicUrls.length === 0 && <Typography variant="body2" color="text.secondary">
                           {translate("share-audience-no-maps")}
                        </Typography>}
                        {publicUrls.map((el) => {
                           return (
                              <Box key={el.url} sx={{ mb: 1 }}>
                                 <Stack spacing={0}>
                                    <Typography variant="subtitle1" sx={{ overflowWrap: "anywhere" }}>{el.title}</Typography>
                                    <CopyAudienceLink url={el.url} />
                                    <AudienceQrCode url={el.url} title={el.title} />
                                 </Stack>
                              </Box>
                           )
                        })}
                     </Grid>

                     {!isNewAudienceSharing && <Grid item xs="auto" sm={2} sx={{ flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'flex-end' }}>
                           <Box sx={{ borderLeft: 1, borderColor: "divider" }}>
                              <Switch disabled={DISABLE_AUDIENCE || loading} checked={Boolean(audienceToggle)} inputProps={{ 'aria-label': translate("share-audience-map") }} onChange={handleChangeAudienceToggle} sx={{ ml: 2, mt: 0.6 }} />
                           </Box>
                        </Box>
                     </Grid>}
                  </Grid>
               </Box>
            </>}
         </>
      </FormDialog >
   )
}

export default ShareDialog
